import _ from "underscore";
import resolve from "ioc/resolve";
import designerUserSettings from "emun.on-demand/lib/designer-user-settings";
import fabric from "emun.on-demand/lib/fabric";
import config from "emun.on-demand/_fabric.config";
import $pageConfig from "webutils/services/page-config";
import $appConfig from "servicestack/config";
import webfonts from "webfonts/webfonts";
import webcolors from "webcolors/webcolors";
import modals from "webmodals/modals";
import Media from "webutils/media/media";
import Design from "emun.on-demand/lib/design";
import DesignLayer from "emun.on-demand/lib/design-layer";
import ssUtils from "webutils/services/util";
import utils from "emun.on-demand/lib/util";
import httpPoller from "webutils/http-poller.service";
import pickRatio from "emun.on-demand/design-ratio-picker";
import ServiceBus from "servicestack/service-bus";
import pickMedia from "emun.on-demand/media/media-picker";
import remoteUpload from "emun.on-demand/media/remote-upload-media-prompt";

const constrainTo = 500;
const sampleDesignJson =
  "http://emun-on-demand.s3.amazonaws.com/designs/mw141/1008_2000/design.json?cache=123";

export default class Designer {
  constructor(scope, element, attrs) {
    const $rootScope = resolve("$rootScope");
    const $localStorage = resolve("$localStorage");
    const d = this;
    let canvasType =
      attrs.canvasType || (config.staticCanvas ? "StaticCanvas" : "Canvas");

    //properties
    d.bus = new ServiceBus();
    d.config = config;
    d.status = "constructing";
    d.mode = config.defaultDesignMode || "Inline";
    d.$scope = scope;
    d.$element = element;
    d.$container = element.parent()[0].parentNode;
    d.$fc = new fabric[canvasType](element[0], {
      preserveObjectStacking: true,
      perPixelTargetFind: true,
      lockScalingFlip: true,
      targetFindTolerance: 6,
      controlsAboveOverlay: true,
      centeredRotation: true,
      centeredScaling: true,
      allowTouchScrolling: false,
      selection: true,
    });
    d.openPanels = {
      add: true,
      layers: true,
      text: true,
      images: true,
      colors: true,
      groups: true,
      clipart: true,
      resize: true,
      pastDesigns: true,
    };
    d.webfonts = webfonts;
    d.webcolors = webcolors;
    d.colorOpen = {};
    d.advOpen = {};
    d.openEditors = {};
    d.designs = [];
    d.listeners = [];
    d.constrainTo = attrs.constrainTo || constrainTo;
    d.packageId = attrs.packageId;
    d.sku = attrs.sku;
    d.label = attrs.label;
    d.productTemplateId = attrs.productTemplateId;
    d.help = false; //!$localStorage['SeenDesignerHelp'];

    //init
    d.registerListeners(d);

    //done
    if (attrs.onDesignerReady) {
      resolve("$parse")(attrs.onDesignerReady)(scope, {
        designer: d,
        scope,
        element,
        attrs,
      });
    } else {
      d.designs = [
        new Design({
          height: 2600,
          width: 2600,
          overscan: 75,
          json: sampleDesignJson,
        }),
      ];
      d.activate(d.designs[0]);
      //d.loadJson(sampleDesignJson);
    }
    $rootScope.$emit("designerReady", { designer: d });
    d.status = "ready";
    d.processingMedia = [];
  }

  render() {
    const $rootScope = resolve("$rootScope");
    const designer = this;
    const canvas = (designer || {}).$fc;
    const design = designer.design;
    if (!designer.rendering) {
      designer.rendering = resolve("$timeout")(() => {
        designer.setClipTos();
        canvas.renderAll();
        designer.rendering = false;
        //console.log('designerRendered', { designer, objects: canvas.getObjects(), bc: canvas.backgroundColor });
        //resolve('$rootScope').$emit('designerRendered', { designer });
      }, 450);
      //console.info('queue rendering', designer.rendering);
    } else {
      //console.warn('rendering already!');
    }
  }

  save(replacePredecessor, productId) {
    const designer = this;
    const $q = resolve("$q");

    let validatePromises = $q.when(1);
    designer.designs.forEach(
      (d) =>
        (validatePromises = validatePromises.then(() => d.validate(designer)))
    );
    designer.status = "validating";
    return validatePromises.then(() => {
      designer.status = "saving";
      let savePromises = designer.designs.map((d) =>
        d.save(replacePredecessor, productId)
      );

      return $q
        .all(savePromises)
        .then((results) => {
          let createDesignPackageRequest = {
            productTemplateId: designer.productTemplateId,
            label: designer.label,
            sku: designer.sku,
            designId: designer.designs[0].id,
            areas: designer.designs.map((d) => ({
              id: d.id,
              euid: d.areaId,
              label: d.label,
              //description: d.description,
              width: d.width,
              height: d.height,
              backgroundColor: d.backgroundColor || d.background,
              design: d,
            })),
          };

          return designer.bus
            .send(`CreateDesignPackage.json`, createDesignPackageRequest)
            .then((response) => {
              designer.packageId = response.data.id;
              designer.packageEuid = response.data.euid;
              designer.packageCode = response.data.packageCode;
              designer.composite = response.data.design;
              return results;
            });
        })
        .then((results) => designer);
    });
  }

  sync(payload) {
    let $rootScope = resolve("$rootScope");
    let designer = this;
    let canvas = (designer || {}).$fc;
    let design = designer.design;
    $rootScope.$emit("designerSyncing", { designer });
    canvas.clear();
    canvas.loadFromJSON(payload || designer.design, () => {
      $rootScope.$emit("designerSynced", { designer });
    });
    //??? designer.record();
  }

  clear() {
    let $rootScope = resolve("$rootScope");
    let designer = this,
      canvas = designer.$fc;
    //console.debug('clear', designer);
    $rootScope.$emit("designerClearing", { designer });
    canvas.clear();
    $rootScope.$emit("designerCleared", { designer });
  }

  resize(width, height) {
    let d = this.design;
    d.width = width;
    d.height = height;
    if (this.layers.background) {
      this.layers.background.width = width;
      this.layers.background.height = height;
    }
    this.activate(d);
  }

  openRemoteUpload() {
    let options = {
      title: "Upload Image...",
      subtitle: "Enter your phone number",
    };
    return remoteUpload(options);
  }

  openRatioPicker() {
    return pickRatio(this.design).then((r) => this.resize(r.width, r.height));
  }

  openMediaPicker() {
    let d = this;
    let options = {
      tags: this.design.tags,
      tagCriteria: this.design.tags,
      context: "Designer",
    };
    pickMedia(options).then((media) => d.addMedia(media));
  }

  upload($files) {
    let d = this;
    const $cart = resolve("$cart"); //smelly
    if ($files && $files.length) {
      let req = {
        request: {
          tags: {},
        },
        files: $files /*,
                request:{
                    tags: [],
                },
                urls: []*/,
      };
      if ($cart.businessModel === "Wholesale" && $cart.purchaser)
        req.request.tags["$RetailerId"] = [$cart.purchaser.id];
      d.status = "uploading";

      d.bus.upload("CreateMedia.json", req).then((res) => {
        let media = res.data.media.map((m) => new Media(m));
        d.addMedia(media);
      });
    }
  }

  activate(design) {
    let $rootScope = resolve("$rootScope");
    let designer = this,
      canvas = designer.$fc;
    if (!design) {
      design = new Design();
    }
    $rootScope.$emit("designerDesignLoading", { designer, design });
    design.status = "activating";
    canvas.overlayImage = null;
    designer.designs.forEach((d) => d.deactivate());
    design.activate(designer);
    // return;
    designer.design = design;
    designer.syncSize();

    design.$hydrated.then(() => designer.loadJson(design));
  }

  loadJson(json) {
    let $rootScope = resolve("$rootScope");
    let designer = this;
    let canvas = (designer || {}).$fc;
    let payload = json;
    let type = "JSON";
    let design = designer.design;

    if (!design) throw new Error("No design is active!");
    if (angular.isString(json)) {
      if (json.indexOf("http") === 0) {
        //console.info('fetching json', json);
        return resolve("$http")
          .get(json)
          .then((res) => {
            if (res.data.payload) {
              //loom32 artificat
              Object.assign(res.data, res.data.payload);
            }
            if (!design.width || !design.height) {
              if (res.data.width || res.data.height) {
                design.width = res.data.width;
                design.height = res.data.height;
                designer.loadJson(res.data);
              } else {
                fabric.util.enlivenObjects(res.data.objects, (members) => {
                  let group = new fabric.Group(members);
                  design.width = group.width;
                  design.height = group.height;
                  designer.loadJson(res.data);
                });
              }
            } else {
              designer.loadJson(res.data);
            }
          });
      } else {
        payload = JSON.parse(json);
        //console.info('parsing json', payload);
      }
    }

    if (design.backgroundColor) {
      payload.backgroundColor =
        payload.background =
        design.background =
          design.backgroundColor;
    }
    //console.debug('designerDesignLoading', { designer, payload, type, bc: design.backgroundColor });
    $rootScope.$emit("designerDesignLoading", { designer, payload, type });
    if (design.status !== "activating") design.status = "loading";
    Design.scrub(payload.objects);
    //let args = {objects: payload.objects, background: payload.background};

    canvas.loadFromJSON(payload, jsonCallback);
    let layerIndex = 0;

    function jsonCallback() {
      designer.render();
      let objects = canvas.getObjects();
      utils.loadFonts(objects);
      designer.layers = DesignLayer.build(canvas, design);

      if (!design.width || !design.height) {
        let group = new fabric.Group(objects.map((o) => o.clone()));
        design.width = group.width;
        design.height = group.height;
      }

      //EBC-Background
      if (
        config.autoAddWhiteBackground &&
        !canvas.backgroundColor &&
        design.activations <= 1 &&
        designer.layers.colors.length === 0
      ) {
        designer.addBackground("#ffffff", true);
      }

      if (design.status === "activating") {
        designer.$$setFirst(design);
      }

      //console.debug('d.changePerspective', design.perspective, designer.defaultPerspectiveIndex);
      //console.debug('d.defaultPerspectiveIndex', designer.defaultPerspectiveIndex);
      designer.changePerspective(
        design.perspective ||
          design.perspectives[
            design.perspectives.length > designer.defaultPerspectiveIndex
              ? designer.defaultPerspectiveIndex
              : 0
          ] || { width: design.width, height: design.height }
      );
      designer.setClipTos();
      designer.recordRecent("jsonCallBack");
      design.status = "ready";
      //designer.setOverlay(designerUserSettings.overlayType);
      $rootScope.$emit("designerDesignLoaded", {
        designer,
        design,
        payload,
        type,
      });
      designer.onDesignLoaded(design, payload, type);
    }
  }

  $$setFirst(design) {
    let designer = this;
    let canvas = (designer || {}).$fc;
    let payload = canvas.toObject(config.extendedProperties);
    payload.objects.forEach((o) => delete o.clipTo);
    design.setFirst(payload);
    //console.debug('design.$$first', design.$$first);
  }

  onDesignLoaded(design, payload, type) {
    let d = this;
    d.mode === "Inline" && d.layers && d.select(d.layers.colors[0]);
  }

  setPerspectiveOpacity(opacity) {
    const designer = this;
    const canvas = designer.$fc;
    let overlayImage = canvas.overlayImage;
    //console.debug('setPerspectiveOpacity', { overlayImage, designer, opacity });
    if (canvas.overlayImage) {
      canvas.overlayImage.setOpacity(opacity / 100);
      designer.render();
    }
  }

  changePerspective(perspective) {
    let designer = this,
      canvas = designer.$fc,
      design = designer.design;
    let url = "";

    if (!perspective)
      perspective = { width: design.width, height: design.height };

    if (perspective.media) {
      if (!perspective.media.img && perspective.media.url) {
        perspective.media = new Media(perspective.media);
        //console.debug('perspective.media', perspective.media);
      }
      if (
        perspective.media.url &&
        perspective.media.url.startsWith(
          "https://s3.amazonaws.com/emun-on-demand/"
        )
      )
        url = perspective.media.img("medium", "png");
      else url = perspective.media.url;
    }

    design.perspective = perspective;
    //console.debug('changePerspective', { design, perspective, url, canvas });
    canvas.overlayImage = canvas.backgroundImage = null;
    if (url === "generate") {
      let scaled = design.scale(designer);
      let buffer = Math.ceil(Math.min(scaled.width, scaled.height) / 250);
      perspective.ignore = true;
      perspective.media.url = url = utils.generateOverlay(
        scaled.width,
        scaled.height,
        scaled.overscan,
        buffer
      );
    }

    let obj = {
      opacity: perspective.opacity || 1,
      height: perspective.height,
      width: perspective.width,
      left: -perspective.x,
      top: -perspective.y,
      //crossOrigin: 'anonymous',
      // Needed to position overlayImage at 0/0
      originX: "left",
      originY: "top",
    };
    let fn =
      perspective.type === "underlay"
        ? "setBackgroundImage"
        : "setOverlayImage";
    //console.log('fn', obj, url);
    const render = () => designer.render();
    resolve("$timeout")(render, 188);
    canvas[fn](url, render, obj);

    designer.syncSize();
  }

  setClipTos() {
    const re = /text/gi;
    let designer = this,
      canvas = designer.$fc,
      design = designer.design;
    if (!canvas.getActiveObject) return;
    let activeObject = canvas.getActiveObject(),
      activeGroup = canvas.getActiveGroup();
    canvas.forEachObject((o) => {
      let layer = utils.splatName(o.layerId);
      //if (layer.isText || layer.isImage || layer.isShape)
      o.set({
        clipTo:
          !activeGroup && o !== activeObject
            ? fabric.util.clipTo.bind(o, design, o)
            : null,
      });
    });
  }

  syncSize() {
    let designer = this,
      canvas = designer.$fc,
      design = designer.design;
    let scaled = (designer.scaled = design.scaled = design.scale(designer));
    canvas.setDimensions(scaled);
    canvas.setZoom(scaled.zoom);
    canvas.absolutePan(new fabric.Point(-scaled.x, -scaled.y));
    //console.debug('syncSize', { scaled, design, perspective: design.perspective });
    //canvas.renderAll();
  }

  addText(value) {
    let $rootScope = resolve("$rootScope");
    let designer = this;
    let canvas = designer.$fc;
    let design = designer.design;
    design.textLayerCounter = 1 + (design.textLayerCounter || 0);
    let perspective = design.perspective || design;
    if (!designer.layers) designer.layers = DesignLayer.build(canvas, design);
    let lastText =
      (designer.layers.text[designer.layers.text.length - 1] || { objects: [] })
        .objects[0] || {};
    let firstColor =
      (designer.layers.colors[0] || { objects: [] }).objects[0] || {};
    let obj = {
      layerId: "EFL-Text_" + design.textLayerCounter,
      lockScalingFlip: true,
      perPixelTargetFind: false,
      //targetFindTolerance: lastText.targetFindTolerance || 40,
      left: design.width / 2,
      top: design.height - 10,
      originX: "center",
      originY: "bottom",
      textAlign: "center",
      textDecoration: lastText.textDecoration || " ",
      scaleX: lastText.scaleX || 1,
      scaleY: lastText.scaleY || 1,
      fontFamily: lastText.fontFamily || "Abel",
      fontSize: lastText.fontSize || design.height * 0.2,
      opacity: 1,
      fill: lastText.fill || firstColor.fill || "#888888",
    };

    if (designer.layers.map[obj.layerId])
      obj.layerId = "EFL-Text_" + new Date().getTime();

    let text = new fabric.Text(
      value || "Text #" + design.textLayerCounter,
      obj
    );

    webfonts.load(text.fontFamily).finally(() => {
      canvas.add(text).setActiveObject(text);
      text.setCoords();
      designer.render();
      let args = {
        designer,
        text,
        layer: new DesignLayer(text, 88, canvas, true),
      };
      designer.layers = DesignLayer.build(canvas, design);
      designer.select(args.layer);
      //console.debug('designerTextAdded', args);
      $rootScope.$emit("designerTextAdded", args);
    });
    //designer.editText({ layer: args.layer, state: 'text', layers: designer.layers.text });
  }

  addBackground(fill, render) {
    let designer = this;
    let canvas = designer.$fc;
    let design = designer.design;

    let rect = new fabric.Rect({
      layerId: config.backgroundLayerId,
      width: design.width,
      height: design.height,
      fill: fill || "#ffffff",
    });
    canvas.insertAt(rect, 0);
    if (render) designer.render();
    let args = {
      designer,
      rect,
      layer: new DesignLayer(rect, 88, canvas, true),
    };
    designer.layers = DesignLayer.build(canvas, design);
    designer.select(args.layer);
    resolve("$rootScope").$emit("designerTextAdded", args);
  }

  addMedia(media) {
    const d = this;
    if (!media) return;
    if (!Array.isArray(media)) return d.addMedia([media]);
    media.forEach((m) => {
      d.processingMedia.push(m);
      let promise =
        m.realm === "Design"
          ? d.addGroupReplace(m)
          : m.isBitmap()
          ? d.addBitmap(m)
          : d.addGroup(m);
      promise.then(() => (d.processingMedia = _.without(d.processingMedia, m)));
    });
  }

  addGroupReplace(media, _options) {
    const designer = this;
    const canvas = designer.$fc;
    const url = media.designUrl();
    return httpPoller({ url, get: true }).then((res) => {
      let d = res.data;
      let objects = d.objects || d.payload.objects;
      Design.scrub(objects);
      let scale = utils.getTranslatedScale(d, designer.design);
      utils.translateObjects(objects, scale);
      designer.loadJson({ objects });
    });
  }
  addGroupAppend(media, _options) {}

  replaceDesign(design, designId) {
    const deferred = resolve("$q").defer();
    Design.get(designId, false).then((media) => {
      const url = media.designUrl();
      httpPoller({ url, get: true }).then((res) => {
        let d = res.data;
        let objects = d.objects || d.payload.objects;
        Design.scrub(objects);
        let scale = utils.getTranslatedScale(d, design);
        utils.translateObjects(objects, scale);
        design.objects = objects;
        deferred.resolve(design);
      }, deferred.reject);
    }, deferred.reject);
    return deferred.promise;
  }

  addGroup(media, _options) {
    let $rootScope = resolve("$rootScope");
    let $q = resolve("$q");
    let designer = this;
    let options = Object.assign(
      {
        fillRatio: 0.75,
      },
      _options
    );
    let canvas = designer.$fc;
    let design = options.design || designer.design;
    let perspective = design.perspective || design;
    let clipartIndex = new Date().getTime(); // (((designer.layers || {}).groups || []).length + 1);
    let url = media.designUrl();
    designer.status = "loading";
    designer.loadingImage = media;
    return httpPoller({ url, get: true }).then((res) => {
      let d = res.data;
      let name = (media.label || "Group") + "_" + clipartIndex;
      let groupId = "EFG-" + name;
      let objects = d.objects || d.payload.objects;
      Design.scrub(objects);

      objects.forEach((o) => {
        let layer = utils.splatName(o.layerId);
        o.originalLayerId = o.layerId;
        o.layerId = `${layer.type}-${name} - ${layer.name}`;
        o.label = layer.name;
        o.groupId = groupId;
      });
      return fabric.util.enlivenObjects(objects, (members) => {
        let group = new fabric.Group(members, {
          label: media.label,
          layerId: groupId,
          left: design.width / 2,
          top: design.height / 2,
          sourcePath: url,
          //backgroundColor: d.background,
          originX: "center",
          originY: "center",
        });
        let contain = {
          width: design.width * options.fillRatio,
          height: design.height * options.fillRatio,
        };
        group.scaleX = group.scaleY = utils.getContainScale(
          group,
          contain,
          8500
        );
        canvas.add(group);
        utils.loadFonts(members);
        designer.render();
        let args = {
          designer,
          group,
          layer: new DesignLayer(group, 88, canvas, true),
        };
        designer.layers = DesignLayer.build(canvas, design);
        designer.mode !== "Inline" && designer.select(args.layer, false);
        $rootScope.$emit("designerClipartAdded", args);
        designer.status = "ready";
        resolve("$timeout")(() => delete designer.loadingImage);
      });
    });
  }

  onDesignReplaced(args) {}

  addBitmap(media, fillRatio) {
    let $rootScope = resolve("$rootScope");
    let designer = this;
    let canvas = designer.$fc;
    let id = ssUtils.uuid();
    let design = designer.design;
    let perspective = design.perspective || design;
    if (!fillRatio) fillRatio = 1;

    let obj = {
      id,
      originalSrc: media.url,
      src: media.img("medium"),
      crossOrigin: "anonymous",
      layerId: "EFI-" + id,
      label: media.label,
      left: design.width / 2,
      top: design.height / 2,
      width: media.width,
      height: media.height,
      originX: "center",
      originY: "center",
    };
    designer.loadingImage = obj;
    console.log("addBitmap", obj, media);
    return httpPoller(obj.src).then(
      (res) => fabric.Image.fromURL(obj.src, cb, obj),
      (res) => {
        console.error("no image", res);
        delete designer.loadingImage;
      }
    );

    function cb(image) {
      let contain = {
        width: design.width * fillRatio,
        height: design.height * fillRatio,
      };
      image.scaleX = image.scaleY = utils.getContainScale(image, contain);
      canvas.add(image);
      designer.render();
      let args = {
        designer,
        image,
        layer: new DesignLayer(image, 88, canvas, true),
      };
      designer.mode !== "Inline" && designer.select(args.layer, false);
      $rootScope.$emit("designerImageAdded", args);
      designer.status = "ready";
      resolve("$timeout")(() => delete designer.loadingImage);
    }
  }

  downloadDesignJson() {
    let $rootScope = resolve("$rootScope");
    let designer = this;
    let json = designer.toJson();
    $rootScope.$emit("downloadingDesignJson", { designer, json });
    location.href = "data:application/octet-stream," + encodeURIComponent(json);
    $rootScope.$emit("designJsonDownloaded", { designer, json });
  }

  registerListeners(designer) {
    let fc = designer.$fc;
    let $rootScope = resolve("$rootScope");
    let $timeout = resolve("$timeout");
    let tryRecord = _.debounce(function () {
      ////console.log('tryRecord', this.toString(), arguments[0]);

      $timeout(() => {
        designer.layers = DesignLayer.build(fc, designer.design);
        designer.record();
      }, 100);
    }, 250);
    let closeLayer = function () {
      designer.setClipTos();
      if (designer.openLayerId) {
        $timeout(() => {
          designer.openLayerId = null;
          $rootScope.$emit("designerOpenLayerClosed", arguments);
        });
      }
    };
    let deleteSelected = (e) => {
      if (
        e.keyCode == 46 &&
        e.target.nodeName !== "INPUT" &&
        e.target.nodeName !== "TEXTAREA"
      ) {
        ////console.warn('deleteSelected', e.target, e);
        let activeObject = fc.getActiveObject(),
          activeGroup = fc.getActiveGroup();
        if (activeObject) {
          modals
            .confirm("Delete the selected objects", "Are you sure?")
            .result.then(() => fc.remove(activeObject));
        } else if (activeGroup) {
          modals
            .confirm("Delete the selected objects", "Are you sure?")
            .result.then(() => {
              let objectsInGroup = activeGroup.getObjects();
              fc.discardActiveGroup();
              objectsInGroup.forEach((o) => fc.remove(o));
            });
        }
      }
    };

    fc.on("mouse:up", (e) => designer.setClipTos());

    fc.on("mouse:down", (e) => {
      designer.setClipTos();
      if (e.target) {
        $timeout(() => {
          let layer = (designer.selected =
            designer.layers.map[e.target.layerId]);
          if ($pageConfig.is("gt-xs"))
            designer.openLayerId =
              designer.openLayerId !== e.target.layerId
                ? e.target.layerId
                : null;
          $rootScope.$emit("designerObjectSelected", { e, layer });
          $rootScope.$emit("designerObjectSelected:" + e.target.layerId, {
            e,
            layer,
          });
          console.warn("designerObjectSelected:" + e.target.layerId, {
            e,
            layer,
          });
        });
      }
    });
    fc.on("selection:cleared", (args) => {
      //console.warn('cleared', args);
      designer.setClipTos();
      designer.recordRecent("selection:cleared");
      $timeout(() => designer.deactivateAll());
    });
    fc.on("object:selected", (args) => {
      designer.recordRecent("object:selected");
    });
    fc.on("selection:created", (args) => {
      designer.recordRecent("selection:created");
    });
    fc.on("object:moving", closeLayer);
    fc.on("object:scaling", closeLayer);
    fc.on("object:rotating", closeLayer);
    fc.on("object:modified", tryRecord.bind("object:modified"));
    fc.on("object:added", tryRecord.bind("object:added"));
    fc.on("object:removed", tryRecord.bind("object:removed"));
    fc.on("path:created", tryRecord.bind("path:created"));

    document.addEventListener("keydown", deleteSelected, false);
    designer.listeners.push(() =>
      document.removeEventListener("keydown", deleteSelected, true)
    );
    designer.registerListener("MediaCreated", (e, args) => {
      //console.warn('MediaCreated', e, args);
      designer.addMedia(args.media.map((m) => new Media(m)));
    });
    designer.registerListener("designerObjectModified", () => {
      designer.render();
      tryRecord(arguments);
    });
    designer.registerListener("designRevived", (e, args) => {
      //console.warn('designRevived', args);
      designer.loadJson(args.payload);
    });
    designer.registerListener(
      "renderDesigner",
      _.debounce((e, args) => {
        designer.render();
      }, 250)
    );
    designer.registerListener(
      "webfontLoaded",
      _.debounce((e, args) => {
        designer.render();
      }, 250)
    );
    designer.registerListener("pageResized", (e, pageConfig) => {
      designer.syncSize();
    });
    designer.registerListener("onTimedout", (e, session) => {
      designer.isDirty = false;
    });
    designer.registerListener("onBeforeUnload", function (e, confirmation) {
      confirmation.message =
        designer.isDirty &&
        "All modifications to your design/item will be lost.";
    });
    designer.registerListener(
      "clipartBrowserOpened",
      (args) => (designer.openLayerId = null)
    );
    designer.registerListener("clipartSelected", function (e, args) {
      let url = `http://emun-on-demand.s3.amazonaws.com/designs/mw141/${args.clipart.id}/design.json`;
      designer.addGroup(url, args.clipart.name);
    });
    designer.registerListener(
      "renderingBrowserOpened",
      (args) => (designer.openLayerId = null)
    );
    designer.registerListener("renderingSelected", function (e, args) {
      let url = `http://emun-on-demand.s3.amazonaws.com/renderings/mw141/${args.rendering.renderingId}/design.json`;
      designer.addGroup(url, args.rendering.cartName, 1);
    });
    designer.onListenersRegistered(fc, $rootScope);
  }

  registerListener(eventName, fn) {
    let $rootScope = resolve("$rootScope");
    this.listeners.push($rootScope.$on(eventName, fn));
  }

  onListenersRegistered(fc, $rootScope) {}

  recordRecent(force) {
    let designer = this;
    designer.layers &&
      designer.layers.all &&
      designer.layers.all.forEach((l) => {
        webcolors.addRecent(l.get("fill"), force);
        webfonts.addRecent(l.get("fontFamily"));
      });
  }

  record(force) {
    let designer = this;
    if (designer.design.recording === "paused" && !force) return false;
    resolve("$timeout")(() => {
      designer.isDirty = designer.design.history.length;
      designer.design.record(
        designer.$fc.toObject(config.extendedProperties),
        force
      );
      designer.setClipTos();
    });
  }

  toJson(propertiesToInclude) {
    return this.$fc.toJSON(propertiesToInclude || config.extendedProperties);
  }

  complexity() {
    return this.$fc.complexity();
  }

  togglePanel(id) {
    this.openPanels[id] = !this.openPanels[id];
  }

  toggleEditor(id) {
    this.openEditors[id] = !this.openEditors[id];
  }

  imageLoading() {
    let designer = this;
    let result = false;
    designer &&
      (designer.loadingImage ||
        (designer.layers &&
          designer.layers.images &&
          designer.layers.images.forEach(
            (i) => (result = result || i.imageLoading())
          )));
    return result;
  }

  canSubmitEmptyText() {
    return (
      this.iLikeItBlank ||
      (this.layers &&
        this.layers.text &&
        this.layers.text.filter((t) => (t.text() || "").length > 0).length)
    );
  }

  setMode(mode) {
    const designer = this;
    designer.mode = mode;
  }

  selectAllObjects() {
    const designer = this;
    const canvas = designer.$fc;
    const objs = canvas
      .getObjects()
      .map((o) => o.set({ active: true, clipTo: null }));

    const group = new fabric.Group(objs, {
      originX: "center",
      originY: "center",
    });

    canvas._activeObject = null;

    canvas.setActiveGroup(group.setCoords());
    designer.render();
  }

  remove(layer, $event) {
    let designer = this;
    designer.openLayerId = null;
    layer.remove().then(() =>
      resolve("$timeout")(() => {
        designer.selected = null;
        designer.openPanels.layers = true;
        designer.render();
      })
    );
    //resolve('$timeout')(() => designer.openLayerId = null, 250);
    $event && $event.stopPropagation();
  }

  mouseenter(layer) {
    return;
    let canvas = this.$fc;
    layer.objects.forEach((o) => {
      o.centeredScaling = true;
      if (o.animating) return;
      o.animating = true;
      let angle = o.angle;
      o.animate("angle", "+=10", {
        from: "-=10",
        easing: fabric.util.ease.easeOutBounce,
        onChange: canvas.renderAll.bind(canvas),
        onComplete() {
          o.animate(
            { angle },
            {
              onChange: canvas.renderAll.bind(canvas),
              onComplete: () => delete o.animating,
              duration: 250,
            }
          );
        },
        duration: 350,
      });
    });
  }

  mouseleave(layer) {
    let canvas = this.$fc;
  }

  getObjects(type) {
    return this.$fc.getObjects(type);
  }

  select(layer, open) {
    let designer = this;
    if (!layer) return;
    designer.selected = layer;
    console.debug("designer.selected", layer);
    layer.select();
    designer.openLayerId = open !== false ? layer.id : null;
    designer.colorOpen = false;
  }

  pause() {
    this.design.pause();
  }

  reset() {
    this.design.reset();
  }

  clear() {
    this.design.clear();
  }

  canUndo() {
    this.design.canUndo();
  }

  undo() {
    this.design.undo() && this.sync();
  }

  canRedo() {
    this.design.canRedo();
  }

  redo() {
    this.design.redo() && this.sync();
  }

  deactivateAll() {
    let designer = this;
    designer.$fc.deactivateAll().renderAll();
    if (designer.selected) {
      designer.selected.advOpen = designer.selected.colorOpen = false;
    }
    designer.selected = null;
    designer.colorOpen = {};
    designer.advOpen = {};
    designer.openLayerId = null;
    designer.openPanels.layers = true;
  }

  $destroy() {
    this.dispose();
  }
  destroy() {
    this.dispose();
  }

  dispose() {
    const $localStorage = resolve("$localStorage");
    const designer = this;
    resolve("$timeout")(() => {
      $localStorage["SeenDesignerHelp"] = true;
      designer.$fc.dispose();

      designer.listeners.forEach((un) => un());
    }, 888);
  }
}
